import { Component, HostBinding, Input, ViewChild } from '@angular/core';
import {
  ActiveLookup,
  BUSINESS_PERSONAL_TYPE,
  LayoutGridType,
  LookupKeys,
  Messages,
  Website,
  WebsiteKeys,
} from '@ag-common-lib/public-api';
import { ModalWindowComponent } from '../modal-window/modal-window.component';
import { ToastrService } from 'ngx-toastr';
import { WebsitesService } from './websites.service';
import { BehaviorSubject } from 'rxjs';
import { validateDxGroups } from 'ag-common-svc/lib/utils/validation';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import { WebsiteFormComponent } from './components/website-form/website-form.component';

@Component({
  selector: 'ag-shr-websites',
  templateUrl: './websites.component.html',
  styleUrls: ['./websites.component.scss'],
  providers: [WebsitesService],
})
export class WebsitesComponent {
  @HostBinding('class') className = 'websites';
  @ViewChild('websiteEditorModalRef', { static: true }) websiteEditorModalComponent: ModalWindowComponent;
  @ViewChild('websiteFormRef', { static: false }) websiteFormComponent: WebsiteFormComponent;

  @Input() parentDbId: string;
  @Input() websites: Website[] = [];
  @Input() hasPermissionsToEdit = true;
  @Input() hasPermissionsToAdd = true;
  @Input() hasPermissionsToDelete = true;
  @Input() gridTitle: string = 'Websites';
  @Input() type: LayoutGridType = LayoutGridType.short;

  @Input()
  set websiteTypesLookup(items: ActiveLookup[]) {
    this._websiteTypesLookup = items;
    this._defaultWebsiteTypeLookup = items?.find(item => item?.isDefault);
  }

  get websiteTypesLookup(): ActiveLookup[] {
    return this._websiteTypesLookup;
  }

  @Input() saveWebsites: (websites: Website[]) => Promise<Website[]>;

  inProgress$ = new BehaviorSubject<boolean>(false);
  protected inEditMode$ = new BehaviorSubject<boolean>(false);

  websiteModalTitle = 'Add / Edit Website';
  hasWebsitesFormChanges: boolean = false;
  protected readonly websiteValidationGroup = 'websiteValidationGroup';
  protected websiteToEdit: Website;
  protected readonly WebsiteKeys = WebsiteKeys;
  protected readonly LookupKeys = LookupKeys;
  protected readonly BaseModelKeys = BaseModelKeys;
  protected readonly LayoutGridType = LayoutGridType;
  protected readonly businessPersonalType = BUSINESS_PERSONAL_TYPE;
  readonly Messages = Messages;
  readonly emptyMessage = 'No Websites Currently Exist';
  private _defaultWebsiteTypeLookup: ActiveLookup;
  private _websiteTypesLookup: ActiveLookup[] = [];

  constructor(
    private toastrService: ToastrService,
    private websitesService: WebsitesService,
  ) {}

  onRowRemoving = e => {
    const websites = this.websites.filter(address => address !== e.key);
    e.cancel = this.updateWebsites(websites);
  };

  onWebsiteTypeChanged = ({ selectedItem }): void => {
    this.websitesService.setSelectedWebsiteTypeChange(selectedItem);
  };

  goToWebsite = url => {
    window.open(url, '_blank');
  };

  protected handleCopyWebsites = ({ row: { data } }) => {
    const copiedUrl = data?.url;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(copiedUrl)
        .then(() => {
          this.toastrService.success('Link Copied!');
        })
        .catch(error => {
          console.error('Error', error);
        });
    }
  };

  protected handleWebsiteSave = async e => {
    this.inProgress$.next(true);

    const isValid = await validateDxGroups([this.websiteValidationGroup]);
    if (!isValid) {
      this.inProgress$.next(false);
      return;
    }

    const websites = this._normalizeWebsites();
    try {
      await this.updateWebsites(websites);

      this.websiteEditorModalComponent.forceCloseModal();
    } catch (error) {
      this.inProgress$.next(false);
    }
  };

  protected handleWebsiteClosePopup = () => {
    return this.websiteFormComponent.onCancelEdit();
  };

  protected handleEditWebsites = e => {
    const data = e?.row?.data;
    this.websiteModalTitle = !data ? 'Add Website' : 'Edit Website';
    this.websiteToEdit = Object.assign(
      {
        [WebsiteKeys.websiteType]: this._defaultWebsiteTypeLookup?.dbId,
      },
      new Website(),
      data,
    );

    this.websiteEditorModalComponent.showModal();
  };

  protected enableEditMode = () => {
    this.inEditMode$.next(true);
  };

  protected disableEditMode = () => {
    this.inEditMode$.next(false);
  };

  private _normalizeWebsites = (): Website[] => {
    if (!this.websiteToEdit) {
      return this.websites;
    }
    let isUpdated = false;
    const normalizedWebsites = this.websites.map(website => {
      if (this.websiteToEdit?.[WebsiteKeys.id] === website?.[WebsiteKeys.id]) {
        isUpdated = true;
        return this.websiteToEdit;
      }
      const normalizedWebsite = Object.assign({}, website);

      return normalizedWebsite;
    });

    if (!isUpdated) {
      normalizedWebsites.push(Object.assign({}, this.websiteToEdit));
    }
    return normalizedWebsites;
  };

  private updateWebsites(websites: Website[]) {
    this.inProgress$.next(true);
    return Promise.all([this.saveWebsites(websites), this.websitesService.onUpdateWebsites()])
      .then(() => {
        this.websites = websites;
        this.toastrService.success('Websites updated!');
      })
      .catch(() => {
        this.toastrService.error('Websites are not updated!');
      })
      .finally(() => {
        this.inProgress$.next(false);
      });
  }
}
